import { ga4Event } from "../../main/utils/analytics";
import { assignedVariants } from "../../main/utils/experiments";
import { getClientId } from "../../main/utils/google-cookies";
import {
  clearLumeEntry,
  currentDevice,
  positiveInt,
  readLumeEntry,
  writeLumeEntry,
} from "../../main/utils/lume-entry";
import type { LumeEntry } from "../../main/utils/lume-entry";
import { apiFetch } from "../api/client";
import { getCredentials, isLoggedIn, subscribeToAuthChanges } from "./auth";

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Um clique e a carga do Lume que ele abriu, com folga para um login pelo meio. */
const SAME_CLICK_MAX_AGE_MS = 30 * 60 * 1000;

/** O uuid do último aluno já gravado neste browser, para não repetir o POST. */
const SENT_KEY = "tm_lume_entry_sent";

let captured: LumeEntry | null = null;
let attempted = false;

/**
 * Antes de o router arrancar: o validateSearch das rotas descarta porta/msg/t.
 * O que vem no URL é regravado no storage para sobreviver a um login que
 * recarrega a página para um URL sem eles.
 */
export function captureEntry(): void {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    params = new URLSearchParams();
  }

  const stored = readLumeEntry();
  const fresh = stored && Date.now() - stored.ts < MAX_AGE_MS ? stored : null;
  const porta = params.get("porta");

  // O login no fim do quiz de /topico acontece sem navegação.
  subscribeToAuthChanges(sendEntryOnce);

  if (!porta) {
    captured = fresh;
    return;
  }

  const messageId = params.get("msg") || null;
  const termId = positiveInt(params.get("t"));
  // Só a porta não chega: dois balões abertos em abas diferentes deixariam a
  // msg de um artigo com o post_id e o page_path do outro. E um link aberto em
  // nova aba sem clique (long-press) não regrava o storage: sem o limite de
  // tempo, herdava a página e o device de um clique antigo da mesma porta.
  const sameClick =
    fresh !== null &&
    Date.now() - fresh.ts < SAME_CLICK_MAX_AGE_MS &&
    fresh.porta === porta &&
    fresh.message_id === messageId &&
    fresh.term_id === termId;
  captured = {
    porta,
    message_id: messageId,
    term_id: termId,
    post_id: sameClick ? fresh.post_id : null,
    page_path: sameClick ? fresh.page_path : null,
    device: sameClick ? fresh.device : currentDevice(),
    experiments: sameClick ? fresh.experiments : assignedVariants(),
    ts: sameClick ? fresh.ts : Date.now(),
  };
  writeLumeEntry(captured);
}

function alreadySent(uuid: string): boolean {
  try {
    return window.localStorage.getItem(SENT_KEY) === uuid;
  } catch {
    return false;
  }
}

function markSent(uuid: string): void {
  try {
    window.localStorage.setItem(SENT_KEY, uuid);
  } catch {
    // sem storage repete-se o POST, que o INSERT IGNORE torna inócuo
  }
}

/**
 * Fire-and-forget: a atribuição nunca atrasa nem parte uma navegação. Uma só
 * tentativa por carga: com a tabela em falta ou a API em erro, cada navegação
 * seria mais um POST e mais uma linha no error_log.
 */
export function sendEntryOnce(): void {
  if (attempted || !isLoggedIn()) return;
  const uuid = getCredentials().uuid || "";
  if (!uuid) return;
  // O clique em storage é de um aluno já gravado; se ficasse, o próximo a
  // entrar neste browser herdava-o.
  if (alreadySent(uuid)) {
    clearLumeEntry();
    captured = null;
    return;
  }

  attempted = true;
  const entry = captured;

  void (async () => {
    try {
      const gaClientId = await getClientId();
      const result = await apiFetch<{ created: boolean; porta: string }>(
        "/api/lume/entry/",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            porta: entry?.porta ?? "desconhecida",
            message_id: entry?.message_id ?? null,
            term_id: entry?.term_id ?? null,
            post_id: entry?.post_id ?? null,
            page_path: entry?.page_path ?? null,
            device: entry?.device ?? currentDevice(),
            experiments: entry?.experiments ?? assignedVariants(),
            ga_client_id: gaClientId,
          }),
        }
      );

      markSent(uuid);
      clearLumeEntry();
      captured = null;

      if (result.created) {
        ga4Event({ event: "set_user_properties", lume_porta: result.porta });
      }
    } catch {
      // fica por gravar; a próxima carga do Lume tenta outra vez
    }
  })();
}
